import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { YearChips, yearsToSeasonParam, yearsLabel } from '../components/YearChips';
import { useCollapseOnScroll } from '../components/useCollapseOnScroll';
import { FilterSheet } from '../components/FilterSheet';

// Public pages live under /club and must read from the anonymous API mirror.
// Derived from the URL rather than passed down as a prop: these files have
// deeply nested components (drilldown sheets, modals, breakdown panels) that
// make their own requests, and threading a prop through every one of them is
// how you end up with `P is not defined` crashing a page on click.
function PUB() {
  return (typeof window !== 'undefined' && window.location.pathname.startsWith('/club'))
    ? '/public' : '';
}


// ── Date formatting helper ────────────────────────────────
function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(d).slice(0, 10); }
}

// ══════════════════════════════════════════════════════════════════════
// Drilldown — tab-specific drilldown renderer
// breakdown shape: { tab, rows?, per_event?, per_match?, summary? }
// ══════════════════════════════════════════════════════════════════════
function Drilldown({ breakdown, loading, activeTab }) {
  const wrap = (children) => (
    <div style={{
      background: '#f8fafc', border: '1px solid #f1f5f9', borderTop: 'none',
      borderRadius: '0 0 12px 12px', marginBottom: 2,
      // NOTE: no `overflow: hidden` here — it would clip the sticky <thead>.
      // The rounded bottom corners are preserved by the border-radius alone.
    }}>{children}</div>
  );
  if (loading) return wrap(<div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: '0.82rem' }}>Loading…</div>);
  if (!breakdown) return wrap(<div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: '0.82rem' }}>No data available</div>);

  const tab = breakdown.tab || activeTab;
  // Sticky drill-down header. The header pins to the top of the drill-down's
  // own scroll box (see `scrollBox` below) — top:0 relative to that box — so it
  // works regardless of the page's filter-bar height and isn't broken by the
  // overflowX:auto wrappers. background must be on the cells for sticky to paint.
  const th = {
    padding: '8px 6px', textAlign: 'center', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
    position: 'sticky', top: 0, zIndex: 20, background: '#1E3A6E',
  };
  const thL = { ...th, textAlign: 'left', padding: '8px 12px' };
  // Shared scroll box for every drill-down table: caps height and scrolls
  // vertically, so long breakdowns scroll under the pinned header.
  const scrollBox = { maxHeight: 360, overflowY: 'auto', overflowX: 'auto' };
  const td = { padding: '7px 6px', textAlign: 'center', color: '#6b7280' };
  const tdL = { padding: '7px 12px', color: '#374151' };
  const tdStrong = { ...td, fontWeight: 600, color: '#1E3A6E' };
  const empty = (msg) => wrap(<div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: '0.82rem' }}>{msg}</div>);

  // ── UCC Points / All-Rounder — per-match grid ───────────
  if (tab === 'points' || tab === 'allrounder') {
    const rows = breakdown.rows || [];
    if (rows.length === 0) return empty('No match data available');
    return wrap(
      <div style={scrollBox}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
            <th style={thL}>Date</th>
            <th style={th}>R</th><th style={th}>B</th><th style={th}>4s</th><th style={th}>6s</th>
            <th style={th}>Ov</th><th style={th}>W</th><th style={th}>C/S/R</th>
            <th style={{ ...th, color: '#F5C518' }}>Pts</th>
          </tr></thead>
          <tbody>
            {rows.map((m, idx) => {
              const bg = idx % 2 === 0 ? 'white' : '#f8fafc';
              const dis = (parseInt(m.catches)||0) + (parseInt(m.stumpings)||0) + (parseInt(m.run_outs)||0);
              return (
                <tr key={m.match_id} style={{ background: bg }}>
                  <td style={tdL}>{fmtDate(m.match_date)}</td>
                  <td style={tdStrong}>{m.runs || 0}</td>
                  <td style={td}>{m.balls || 0}</td>
                  <td style={td}>{m.fours || 0}</td>
                  <td style={td}>{m.sixes || 0}</td>
                  <td style={td}>{parseFloat(m.overs||0).toFixed(1)}</td>
                  <td style={tdStrong}>{m.wickets || 0}</td>
                  <td style={td}>{dis}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'center', fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, color: '#1E3A6E', fontSize: '0.9rem' }}>
                    {m.total_pts == null ? '—' : (parseFloat(m.total_pts)||0).toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Best Batsman — every innings ────────────────────────
  if (tab === 'batting') {
    const rows = breakdown.rows || [];
    if (rows.length === 0) return empty('No innings played');
    return wrap(
      <div style={scrollBox}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
            <th style={thL}>Date</th>
            <th style={th}>R</th><th style={th}>B</th><th style={th}>4s</th><th style={th}>6s</th>
            <th style={th}>SR</th>
            <th style={thL}>How Out</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const bg = i % 2 === 0 ? 'white' : '#f8fafc';
              // Retired (hurt or out) is not a dismissal — the innings shows
              // an asterisk and reads "retired hurt", not a wicket. Every
              // other renderer in the app already treats it this way.
              const notOut = r.dismissal === 'not_out'
                || String(r.dismissal || '').startsWith('retired');
              const runsColor = (r.runs||0) >= 50 ? '#16a34a' : (r.runs === 0 && !notOut) ? '#dc2626' : '#1E3A6E';
              const dispDismissal = notOut ? 'not out'
                : (r.dismissal_text || (r.dismissal || '').replace(/_/g, ' ') || '—');
              return (
                <tr key={`${r.match_id}-${r.innings}-${i}`} style={{ background: bg }}>
                  <td style={tdL}>{fmtDate(r.match_date)}</td>
                  <td style={{ ...tdStrong, color: runsColor }}>{r.runs || 0}{notOut ? '*' : ''}</td>
                  <td style={td}>{r.balls || 0}</td>
                  <td style={td}>{r.fours || 0}</td>
                  <td style={td}>{r.sixes || 0}</td>
                  <td style={td}>{r.strike_rate ? parseFloat(r.strike_rate).toFixed(1) : '—'}</td>
                  <td style={{ padding: '7px 12px', textAlign: 'left', color: notOut ? '#16a34a' : '#6b7280', fontStyle: notOut ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                    {dispDismissal}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Best Bowler — every spell ───────────────────────────
  if (tab === 'bowling') {
    const rows = breakdown.rows || [];
    if (rows.length === 0) return empty('No bowling spells');
    return wrap(
      <div style={scrollBox}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
            <th style={thL}>Date</th>
            <th style={th}>Ov</th><th style={th}>R</th><th style={th}>W</th>
            <th style={th}>M</th><th style={th}>Econ</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const bg = i % 2 === 0 ? 'white' : '#f8fafc';
              const econ = parseFloat(r.economy||0);
              const econColor = econ === 0 ? '#6b7280' : econ < 6 ? '#16a34a' : econ < 8 ? '#d97706' : '#dc2626';
              return (
                <tr key={`${r.match_id}-${r.innings}-${i}`} style={{ background: bg }}>
                  <td style={tdL}>{fmtDate(r.match_date)}</td>
                  <td style={td}>{parseFloat(r.overs||0).toFixed(1)}</td>
                  <td style={td}>{r.runs || 0}</td>
                  <td style={{ ...tdStrong, color: (r.wickets||0) >= 3 ? '#16a34a' : '#1E3A6E' }}>{r.wickets || 0}</td>
                  <td style={td}>{r.maidens || 0}</td>
                  <td style={{ ...td, color: econColor, fontWeight: 600 }}>{econ.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Max MOM — matches where he was MOM ──────────────────
  if (tab === 'motm') {
    const rows = breakdown.rows || [];
    if (rows.length === 0) return empty('No MOM awards yet');
    return wrap(
      <div style={scrollBox}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
            <th style={thL}>Date</th>
            <th style={thL}>Result</th>
            <th style={th}>Team A</th>
            <th style={th}>Team B</th>
            <th style={thL}>Notes</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.match_id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                <td style={tdL}>{fmtDate(r.match_date)}</td>
                <td style={{ padding: '7px 12px', color: '#1E3A6E', fontWeight: 600 }}>{r.winner_team || '—'}</td>
                <td style={td}>{r.team_a_score || '—'}</td>
                <td style={td}>{r.team_b_score || '—'}</td>
                <td style={{ padding: '7px 12px', color: r.is_joint ? '#d97706' : '#6b7280', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                  {r.is_joint ? `Joint with ${r.joint_partner || 'another'}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Best Captain — wins in green at top, losses in red at bottom ────
  if (tab === 'captain') {
    const rows = (breakdown.rows || []).filter(r => r.outcome !== 'draw');  // exclude draws from list
    const summary = breakdown.summary || {};
    if (rows.length === 0) return empty('No matches captained with a decisive result');
    return wrap(
      <>
        <div style={{ padding: '10px 16px', background: '#f1f5f9', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem' }}>
          <span style={{ color: '#16a34a' }}>✓ {summary.wins || 0} Wins</span>
          <span style={{ color: '#dc2626' }}>✗ {summary.losses || 0} Losses</span>
          {summary.draws ? <span style={{ color: '#6b7280', fontSize: '0.72rem' }}>({summary.draws} drawn/NR excluded)</span> : null}
        </div>
        <div style={scrollBox}>
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
              <th style={thL}>Date</th>
              <th style={th}>Result</th>
              <th style={thL}>My Team</th>
              <th style={thL}>Opponent Team</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const isWin = r.outcome === 'win';
                const bg = isWin ? '#f0fdf4' : '#fef2f2';
                const color = isWin ? '#16a34a' : '#dc2626';
                return (
                  <tr key={r.match_id} style={{ background: bg }}>
                    <td style={tdL}>{fmtDate(r.match_date)}</td>
                    <td style={{ padding: '7px 6px', textAlign: 'center', color, fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '0.85rem' }}>{isWin ? 'WIN' : 'LOSS'}</td>
                    <td style={{ padding: '7px 12px', color: '#1E3A6E', fontWeight: 600 }}>{r.my_team || '—'}</td>
                    <td style={{ padding: '7px 12px', color: '#6b7280' }}>{r.opponent_team || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  // ── Best Fielder — 2020+ per-event, pre-2020 per-match ───
  if (tab === 'fielding') {
    const perEvent = breakdown.per_event || [];
    const perMatch = breakdown.per_match || [];
    if (perEvent.length === 0 && perMatch.length === 0) return empty('No fielding dismissals');
    return wrap(
      <div style={scrollBox}>
        {perEvent.length > 0 && (
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
              <th style={thL}>Date</th>
              <th style={thL}>Batsman Dismissed</th>
              <th style={th}>Type</th>
              <th style={thL}>Bowler</th>
            </tr></thead>
            <tbody>
              {perEvent.map((r, i) => (
                <tr key={`ev-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={tdL}>{fmtDate(r.match_date)}</td>
                  <td style={{ padding: '7px 12px', color: '#1E3A6E', fontWeight: 600 }}>{r.batsman_name || '—'}</td>
                  <td style={{ ...td, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: '#16a34a' }}>{r.dismiss_type || '—'}</td>
                  <td style={{ padding: '7px 12px', color: '#6b7280' }}>{r.bowler_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {perMatch.length > 0 && (
          <>
            {perEvent.length > 0 && <div style={{ padding: '8px 16px', background: '#f1f5f9', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Pre-2020 (per-match summary)</div>}
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
                <th style={thL}>Date</th>
                <th style={th}>Catches</th>
                <th style={th}>Stumpings</th>
                <th style={th}>Run-outs</th>
              </tr></thead>
              <tbody>
                {perMatch.map((r, i) => (
                  <tr key={`m-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={tdL}>{fmtDate(r.match_date)}</td>
                    <td style={tdStrong}>{r.catches || 0}</td>
                    <td style={td}>{r.stumpings || 0}</td>
                    <td style={td}>{r.run_outs || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  // ── Wood Striker — 2020+ per-event, pre-2020 per-match ──
  if (tab === 'woodstrike') {
    const perEvent = breakdown.per_event || [];
    const perMatch = breakdown.per_match || [];
    if (perEvent.length === 0 && perMatch.length === 0) return empty('No bowled wickets');
    return wrap(
      <div style={scrollBox}>
        {perEvent.length > 0 && (
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
              <th style={thL}>Date</th>
              <th style={th}>Over.Ball</th>
              <th style={thL}>Batsman Bowled</th>
              <th style={th}>Runs</th>
            </tr></thead>
            <tbody>
              {perEvent.map((r, i) => (
                <tr key={`ev-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={tdL}>{fmtDate(r.match_date)}</td>
                  <td style={{ ...td, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>
                    {(parseInt(r.over_number)||0) + 1}.{r.ball_number || '—'}
                  </td>
                  <td style={{ padding: '7px 12px', color: '#1E3A6E', fontWeight: 600 }}>{r.batsman_name || '—'}</td>
                  <td style={td}>{r.runs_at_dismissal == null ? '—' : r.runs_at_dismissal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {perMatch.length > 0 && (
          <>
            {perEvent.length > 0 && <div style={{ padding: '8px 16px', background: '#f1f5f9', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Pre-2020 (per-match summary)</div>}
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
                <th style={thL}>Date</th>
                <th style={th}>Bowled</th>
                <th style={thL}>Batsmen Bowled</th>
              </tr></thead>
              <tbody>
                {perMatch.map((r, i) => (
                  <tr key={`m-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={tdL}>{fmtDate(r.match_date)}</td>
                    <td style={tdStrong}>{r.bowled_count || 0}</td>
                    <td style={{ padding: '7px 12px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{r.batsmen_bowled || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  // ── Max Maidens — 2020+ per-over, pre-2020 per-spell ────
  if (tab === 'maidens') {
    const perEvent = breakdown.per_event || [];
    const perMatch = breakdown.per_match || [];
    if (perEvent.length === 0 && perMatch.length === 0) return empty('No maiden overs');
    return wrap(
      <div style={scrollBox}>
        {perEvent.length > 0 && (
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
              <th style={thL}>Date</th>
              <th style={th}>Innings</th>
              <th style={th}>Over</th>
            </tr></thead>
            <tbody>
              {perEvent.map((r, i) => (
                <tr key={`ev-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={tdL}>{fmtDate(r.match_date)}</td>
                  <td style={td}>{r.innings || 1}</td>
                  <td style={{ ...tdStrong, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>Over {(parseInt(r.over_number)||0) + 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {perMatch.length > 0 && (
          <>
            {perEvent.length > 0 && <div style={{ padding: '8px 16px', background: '#f1f5f9', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Pre-2020 (per-spell summary)</div>}
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#1E3A6E', color: 'white' }}>
                <th style={thL}>Date</th>
                <th style={th}>Ov</th><th style={th}>R</th><th style={th}>W</th>
                <th style={th}>Maidens</th>
              </tr></thead>
              <tbody>
                {perMatch.map((r, i) => (
                  <tr key={`m-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={tdL}>{fmtDate(r.match_date)}</td>
                    <td style={td}>{parseFloat(r.overs||0).toFixed(1)}</td>
                    <td style={td}>{r.runs || 0}</td>
                    <td style={td}>{r.wickets || 0}</td>
                    <td style={tdStrong}>{r.maidens || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  return empty('No drilldown available for this tab');
}
// ── Excel export using SheetJS CDN ───────────────────────
function exportToExcel(rows, filename) {
  import('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js')
    .catch(() => {})
    .finally(() => {
      if (!window.XLSX) {
        // Load via script tag
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.onload = () => doExport(rows, filename);
        document.head.appendChild(script);
      } else {
        doExport(rows, filename);
      }
    });
}

function doExport(rowsOrSheets, filename) {
  try {
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();

    // Multi-sheet shape: array of {name, rows}.
    // Single-sheet shape: plain array of row objects (legacy).
    const isMulti = Array.isArray(rowsOrSheets) && rowsOrSheets.length > 0
      && rowsOrSheets[0] && typeof rowsOrSheets[0] === 'object'
      && 'name' in rowsOrSheets[0] && 'rows' in rowsOrSheets[0];

    if (isMulti) {
      rowsOrSheets.forEach(sheet => {
        const ws = XLSX.utils.json_to_sheet(sheet.rows || []);
        const cols = Object.keys((sheet.rows || [])[0] || {}).map(k => ({ wch: Math.max(k.length, 10) }));
        ws['!cols'] = cols;
        // Sheet names have a 31-char limit and forbidden chars
        const safeName = String(sheet.name).replace(/[\\\/\?\*\[\]:]/g, '_').slice(0, 31) || 'Sheet';
        XLSX.utils.book_append_sheet(wb, ws, safeName);
      });
    } else {
      const rows = rowsOrSheets || [];
      const ws = XLSX.utils.json_to_sheet(rows);
      const cols = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 10) }));
      ws['!cols'] = cols;
      XLSX.utils.book_append_sheet(wb, ws, 'Points');
    }

    XLSX.writeFile(wb, filename);
  } catch (e) {
    alert('Export failed: ' + e.message);
  }
}

// ── Tab definitions ───────────────────────────────────────
const TABS = [
  { id: 'points',      label: '🏆 UCC Points',      key: 'total_pts' },
  { id: 'batting',     label: '🏏 Best Batsman',    key: 'total_runs' },
  { id: 'bowling',     label: '🎳 Best Bowler',     key: 'total_wickets' },
  { id: 'fielding',    label: '🧤 Best Fielder',    key: 'total_dismissals' },
  { id: 'allrounder',  label: '⚡ All-Rounder',     key: 'allrounder_pct' },
  { id: 'motm',        label: '⭐ Max MOM',         key: 'motm_count' },
  { id: 'captain',     label: '🎖 Best Captain',    key: 'captain_wins' },
  { id: 'woodstrike',  label: '🪵 Wood Striker',    key: 'bowled_wickets' },
  { id: 'maidens',     label: '🔒 Max Maidens',     key: 'total_maidens' },
];

// ── Medal colors ──────────────────────────────────────────
const medalColors = ['#F5C518', '#C0C0C0', '#CD7F32'];
const medalEmojis = ['🥇', '🥈', '🥉'];

export function LeaderboardsPage({ publicMode = false }) {
  const { member, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('points');
  // Session 16 — multi-year support. yearsState is the chip UI state;
  // selectedSeason is the derived query param string ('all' | '2026' | '2024,2025,2026').
  const [yearsState, setYearsState] = useState({ mode: 'single', value: 2026 });
  const selectedSeason = yearsToSeasonParam(yearsState);
  const AVAILABLE_YEARS = [2026,2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013];
  // UCC Points exist only from 2022 onward (the points formula was defined then).
  // For the Points tab, restrict the year chips to 2022+. All other tabs keep
  // the full range — they have legitimate data going back to 2013 after the
  // legacy import.
  const POINTS_MIN_YEAR = 2022;
  const availableYearsForTab = activeTab === 'points'
    ? AVAILABLE_YEARS.filter(y => y >= POINTS_MIN_YEAR)
    : AVAILABLE_YEARS;
  const [viewMode, setViewMode] = useState('season'); // season | match
  // Session 16 Pass 2 — scroll-collapse + filter modal
  const collapsed = useCollapseOnScroll(120);
  const [filterOpen, setFilterOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [data, setData] = useState([]);
  const [woodStrikerData, setWoodStrikerData] = useState([]);
  const [maidensData, setMaidensData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMember, setExpandedMember] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [liveMatchId, setLiveMatchId] = useState(null);
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);

  // Load matches list — scoped to selectedSeason. Pre-2022 matches have no import_count
  // but DO have motm rows after Phase C backfill; include anything with either.
  useEffect(() => {
    // For single year: /matches?year=2021&limit=50
    // For multi-year (e.g. "2024,2025,2026") or "all": fetch each year and merge
    const years = (selectedSeason === 'all')
      ? AVAILABLE_YEARS
      : String(selectedSeason).split(',').map(y => parseInt(y, 10)).filter(Boolean);
    if (years.length === 0) return;

    Promise.all(years.map(y =>
      api.get(`${PUB()}/matches?year=${y}&limit=50`).then(r => r.data.matches || []).catch(() => [])
    )).then(lists => {
      const all = [].concat(...lists)
        .filter(m => m.import_count > 0 || m.motm_winner_id || m.data_source === 'ucc_live')  // imported, backfilled MOM, or live-scored
        .sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
      setMatches(all);
      if (all.length > 0) {
        setSelectedMatch(prev => all.find(m => m.id === prev) ? prev : all[0].id);
      } else {
        setSelectedMatch('');
      }
    });
  }, [selectedSeason]);

  // SSE: connect to live stream for real-time updates
  useEffect(() => {
    // Check live status on mount
    api.get('/live/status').then(r => {
      setIsLive(r.data.isLive || false);
      setLiveMatchId(r.data.activeMatchId || null);
    }).catch(() => {});

    // Connect to SSE stream
    const token = localStorage.getItem('ucc_token') || sessionStorage.getItem('ucc_token') || '';
    const evtSource = new EventSource(`/api/live/stream?token=${token}`);

    evtSource.addEventListener('connected', () => {
      console.log('[Live] SSE connected');
    });

    evtSource.addEventListener('scorecard_updated', (e) => {
      const payload = JSON.parse(e.data);
      setIsLive(true);
      setLiveMatchId(payload.matchId);
      setLastLiveUpdate(new Date(payload.timestamp));
      // Auto-refresh leaderboard data
      const params = viewMode === 'match' && selectedMatch
        ? `?matchId=${selectedMatch}`
        : `?season=${selectedSeason}`;
      api.get(`${PUB()}/leaderboards/points${params}`)
        .then(r => setData(r.data.points || []))
        .catch(() => {});
    });

    evtSource.addEventListener('match_ended', () => {
      setIsLive(false);
      setLiveMatchId(null);
      // Final refresh
      const params = viewMode === 'match' && selectedMatch
        ? `?matchId=${selectedMatch}`
        : `?season=${selectedSeason}`;
      api.get(`${PUB()}/leaderboards/points${params}`)
        .then(r => setData(r.data.points || []))
        .catch(() => {});
    });

    evtSource.onerror = () => {
      // SSE will auto-reconnect — no action needed
    };

    return () => evtSource.close();
  }, []);

  // Load leaderboard data
  useEffect(() => {
    setLoading(true);
    const params = viewMode === 'match' && selectedMatch
      ? `?matchId=${selectedMatch}`
      : `?season=${selectedSeason}`;
    api.get(`${PUB()}/leaderboards/points${params}`)
      .then(r => setData(r.data.points || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
    api.get(`${PUB()}/leaderboards/wood-striker${params}`)
      .then(r => setWoodStrikerData(r.data.data || [])).catch(() => {});
    api.get(`${PUB()}/leaderboards/maidens${params}`)
      .then(r => setMaidensData(r.data.data || [])).catch(() => {});
  }, [viewMode, selectedMatch, selectedSeason]);

  // Sort data by active tab
  // All-rounder: charter formula = (bat% + bowl% + field%) / 3
  // where each % = player's total / sum of all players' total × 100
  const totalRuns = data.reduce((s,r) => s+(parseFloat(r.total_runs)||0), 0) || 1;
  const totalWkts = data.reduce((s,r) => s+(parseFloat(r.total_wickets)||0), 0) || 1;
  const totalDis  = data.reduce((s,r) => s+(parseInt(r.total_catches)||0)+(parseInt(r.total_stumpings)||0)+(parseInt(r.total_run_outs)||0), 0) || 1;

  function allRounderPct(row) {
    const batPct  = ((parseFloat(row.total_runs)||0) / totalRuns) * 100;
    const bowlPct = ((parseFloat(row.total_wickets)||0) / totalWkts) * 100;
    const fieldDis = (parseInt(row.total_catches)||0)+(parseInt(row.total_stumpings)||0)+(parseInt(row.total_run_outs)||0);
    const fieldPct = (fieldDis / totalDis) * 100;
    return (batPct + bowlPct + fieldPct) / 3;
  }

  const sorted = [...data].sort((a, b) => {
    switch (activeTab) {
      case 'batting':    return (parseFloat(b.total_runs)||0) - (parseFloat(a.total_runs)||0);
      case 'bowling':    return (parseFloat(b.total_wickets)||0) - (parseFloat(a.total_wickets)||0);
      case 'fielding': {
        const da = (parseInt(a.total_catches)||0)+(parseInt(a.total_stumpings)||0)+(parseInt(a.total_run_outs)||0);
        const db = (parseInt(b.total_catches)||0)+(parseInt(b.total_stumpings)||0)+(parseInt(b.total_run_outs)||0);
        return db - da;
      }
      case 'allrounder': return allRounderPct(b) - allRounderPct(a);
      case 'motm':       return (parseInt(b.motm_count)||0) - (parseInt(a.motm_count)||0);
      case 'captain':    return (parseInt(b.captain_wins)||0) - (parseInt(a.captain_wins)||0);
      default:           return (parseFloat(b.total_pts)||0) - (parseFloat(a.total_pts)||0);
    }
  });

  async function fetchDrilldown(memberId, tab) {
    setBreakdownLoading(true);
    try {
      const r = await api.get(`${PUB()}/leaderboards/drilldown?tab=${tab}&memberId=${memberId}&season=${selectedSeason}`);
      setBreakdown(r.data || {});
    } catch { setBreakdown({ tab, rows: [] }); }
    finally { setBreakdownLoading(false); }
  }

  async function loadBreakdown(memberId) {
    if (expandedMember === memberId) { setExpandedMember(null); setBreakdown(null); return; }
    setExpandedMember(memberId);
    await fetchDrilldown(memberId, activeTab);
  }

  // When user switches tabs while a row is expanded, re-fetch with new tab shape
  useEffect(() => {
    if (expandedMember) fetchDrilldown(expandedMember, activeTab);
  }, [activeTab]);

  // When user switches TO the Points tab and has a pre-2022 year selected,
  // snap to a valid one (Points has no data before 2022).
  useEffect(() => {
    if (activeTab !== 'points') return;
    setYearsState(prev => {
      if (prev.mode === 'all') return prev; // 'All Time' stays — backend skips empty years anyway
      if (prev.mode === 'single') {
        return prev.value < POINTS_MIN_YEAR ? { mode: 'single', value: 2026 } : prev;
      }
      if (prev.mode === 'multi') {
        const kept = (prev.values || []).filter(y => y >= POINTS_MIN_YEAR);
        if (kept.length === prev.values.length) return prev;
        if (kept.length === 0) return { mode: 'single', value: 2026 };
        if (kept.length === 1) return { mode: 'single', value: kept[0] };
        return { mode: 'multi', values: kept };
      }
      return prev;
    });
  }, [activeTab]);

  async function handleExport() {
    setExporting(true);
    try {
      const params = viewMode === 'match' && selectedMatch
        ? `?matchId=${selectedMatch}`
        : `?season=${selectedSeason}`;
      const r = await api.get(`${PUB()}/leaderboards/export${params}`);
      // Load XLSX if needed
      if (!window.XLSX) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      doExport(r.data.sheets || r.data.rows, r.data.filename);
    } catch (e) {
      alert('Export failed: ' + (e?.response?.data?.error || e.message || 'Unknown error'));
    } finally { setExporting(false); }
  }

  function getStatValue(row, tab) {
    switch (tab) {
      case 'batting':  return { value: parseInt(row.total_runs)||0, label: 'runs' };
      case 'bowling':  return { value: parseInt(row.total_wickets)||0, label: 'wkts' };
      case 'fielding': {
        const d = (parseInt(row.total_catches)||0)+(parseInt(row.total_stumpings)||0)+(parseInt(row.total_run_outs)||0);
        return { value: d, label: 'dis' };
      }
      case 'allrounder': return { value: allRounderPct(row).toFixed(2), label: 'AR%' };
      case 'motm':    return { value: parseInt(row.motm_count)||0, label: 'MOM' };
      case 'captain': return { value: parseInt(row.captain_wins)||0, label: 'wins' };
      default:        return { value: (parseFloat(row.total_pts)||0).toFixed(1), label: 'pts' };
    }
  }

  const selectedMatchData = matches.find(m => m.id === selectedMatch);
  const isMe = (row) => row.id === member?.id;

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Sticky filter block — collapses into compact bar when user scrolls past threshold */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        {collapsed ? (
          /* COLLAPSED — compact bar */
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)', lineHeight: 1.1 }}>
                Leaderboards
              </div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {yearsLabel(yearsState)} · {TABS.find(t=>t.id===activeTab)?.label || 'Points'} · {viewMode === 'match' ? 'Per Match' : 'Season'}
              </div>
            </div>
            <button onClick={() => setFilterOpen(true)}
              style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 14,
                padding: '5px 12px', color: '#1E3A6E',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
              ↕ Change
            </button>
          </div>
        ) : (
          /* EXPANDED — full filters */
          <>
          <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.6rem', color: 'var(--navy)' }}>Leaderboards</div>
              {isLive && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#dc2626', color: 'white', padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, animation: 'pulse 1.5s infinite' }}>
                  <span style={{ width: 7, height: 7, background: 'white', borderRadius: '50%', display: 'inline-block' }} />
                  LIVE
                </div>
              )}
              {isLive && lastLiveUpdate && (
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                  Updated {lastLiveUpdate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>SEASON</div>
            <YearChips years={yearsState} availableYears={availableYearsForTab} onChange={setYearsState} />
          </div>

      {/* View mode toggle + Export */}
      <div style={{ padding: '12px 16px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3, flex: 1 }}>
          {[{v:'season',l:'Season Total'},{v:'match',l:'Per Match'}].map(opt => (
            <button key={opt.v} onClick={() => setViewMode(opt.v)}
              style={{
                flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                background: viewMode === opt.v ? 'white' : 'transparent',
                color: viewMode === opt.v ? '#1E3A6E' : '#6b7280',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                fontSize: '0.82rem', cursor: 'pointer',
                boxShadow: viewMode === opt.v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}>
              {opt.l}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button onClick={handleExport} disabled={exporting}
            style={{
              padding: '8px 12px', borderRadius: 10, border: '1px solid #1E3A6E',
              background: 'white', color: '#1E3A6E',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
              fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
            {exporting ? '⏳' : '📥'} Excel
          </button>
        )}
      </div>

      {/* Match selector */}
      {viewMode === 'match' && (
        <div style={{ padding: '8px 16px' }}>
          <select className="form-control" value={selectedMatch}
            onChange={e => setSelectedMatch(e.target.value)}
            style={{ fontSize: '0.85rem' }}>
            {matches.map(m => (
              <option key={m.id} value={m.id}>
                {new Date(m.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — {m.match_type}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div style={{ overflowX: 'auto', padding: '8px 16px 12px' }}>
        <div style={{ display: 'flex', gap: 6, minWidth: 'max-content' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none',
                background: activeTab === tab.id ? '#1E3A6E' : '#f1f5f9',
                color: activeTab === tab.id ? 'white' : '#6b7280',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
                borderBottom: activeTab === tab.id ? '2px solid #F5C518' : '2px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
          </>
        )}
      </div>
      {/* End sticky filter block */}

      {/* Filter modal — opens when user taps "↕ Change" on collapsed bar */}
      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Filters">
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>SEASON</div>
        <YearChips years={yearsState} availableYears={availableYearsForTab} onChange={setYearsState} />
        <div style={{ height: 14 }} />
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>VIEW</div>
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 10, padding: 3 }}>
          {[{v:'season',l:'Season Total'},{v:'match',l:'Per Match'}].map(opt => (
            <button key={opt.v} onClick={() => setViewMode(opt.v)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
                background: viewMode === opt.v ? 'white' : 'transparent',
                color: viewMode === opt.v ? '#1E3A6E' : '#6b7280',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                fontSize: '0.82rem', cursor: 'pointer',
                boxShadow: viewMode === opt.v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}>{opt.l}</button>
          ))}
        </div>
        {viewMode === 'match' && (
          <>
            <div style={{ height: 14 }} />
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>MATCH</div>
            <select className="form-control" value={selectedMatch}
              onChange={e => setSelectedMatch(e.target.value)}
              style={{ fontSize: '0.85rem', width: '100%' }}>
              {matches.map(m => (
                <option key={m.id} value={m.id}>
                  {new Date(m.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — {m.match_type}
                </option>
              ))}
            </select>
          </>
        )}
        <div style={{ height: 14 }} />
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>CATEGORY</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '6px 12px', borderRadius: 18, border: 'none',
                background: activeTab === tab.id ? '#1E3A6E' : '#f1f5f9',
                color: activeTab === tab.id ? 'white' : '#6b7280',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                fontSize: '0.76rem', cursor: 'pointer', whiteSpace: 'nowrap',
              }}>{tab.label}</button>
          ))}
        </div>
      </FilterSheet>

      {/* All-Rounder formula explainer banner */}
      {activeTab === 'allrounder' && !loading && sorted.length > 0 && (
        <div style={{
          margin: '0 16px 8px',
          background: 'linear-gradient(135deg, #1E3A6E 0%, #2d5699 100%)',
          borderRadius: 12, padding: '10px 14px',
          fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)',
          lineHeight: 1.6,
        }}>
          <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, color: '#F5C518', fontSize: '0.8rem', marginBottom: 4 }}>
            ⚡ How All-Rounder % is calculated
          </div>
          <div><strong style={{color:'white'}}>AR%</strong> = (Bat% + Bowl% + Field%) ÷ 3</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
            Bat% = your runs ÷ total runs by all players × 100
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)' }}>
            Bowl% = your wickets ÷ total wickets × 100
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)' }}>
            Field% = your dismissals ÷ total dismissals × 100
          </div>
          <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
            Season totals — Runs: {totalRuns} · Wickets: {totalWkts} · Dismissals: {totalDis}
          </div>
        </div>
      )}

      {/* Leaderboard list */}
      <div style={{ padding: '12px 16px' }}>

        {/* Wood Striker Tab */}
        {activeTab === 'woodstrike' && (
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 12 }}>
              Bowled wickets — clean bowled dismissals by each bowler
            </div>
            {woodStrikerData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No data yet</div>
            ) : woodStrikerData.map((row, i) => {
              const isExpanded = expandedMember === row.id;
              return (
              <div key={row.id || i}>
                <div onClick={() => loadBreakdown(row.id)}
                  style={{ background: 'white', borderRadius: isExpanded ? '12px 12px 0 0' : 12, padding: '12px 14px', marginBottom: isExpanded ? 0 : 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < 3 ? medalColors[i] : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: i < 3 ? '1rem' : '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                    {i < 3 ? medalEmojis[i] : i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1E3A6E' }}>{row.display_name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{row.ucc_id}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.3rem', color: '#1E3A6E' }}>{row.bowled_wickets}</div>
                    <div style={{ fontSize: '0.62rem', color: '#6b7280' }}>Bowled</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#6b7280' }}>{row.total_wickets}</div>
                    <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>Total Wkts</div>
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0 }}>
                    {isExpanded ? '▲' : '▼'}
                  </div>
                </div>
                {isExpanded && <div style={{ marginBottom: 8 }}><Drilldown breakdown={breakdown} loading={breakdownLoading} activeTab={activeTab} /></div>}
              </div>
              );
            })}
          </div>
        )}

        {/* Maiden Overs Tab */}
        {activeTab === 'maidens' && (
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 12 }}>
              Most maiden overs bowled — season total
            </div>
            {maidensData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No data yet</div>
            ) : maidensData.map((row, i) => {
              const isExpanded = expandedMember === row.id;
              return (
              <div key={row.id || i}>
                <div onClick={() => loadBreakdown(row.id)}
                  style={{ background: 'white', borderRadius: isExpanded ? '12px 12px 0 0' : 12, padding: '12px 14px', marginBottom: isExpanded ? 0 : 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < 3 ? medalColors[i] : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: i < 3 ? '1rem' : '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                    {i < 3 ? medalEmojis[i] : i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1E3A6E' }}>{row.display_name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{row.ucc_id}{row.matches_bowled ? ` · ${row.matches_bowled} matches` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.3rem', color: '#1E3A6E' }}>{row.total_maidens}</div>
                    <div style={{ fontSize: '0.62rem', color: '#6b7280' }}>Maidens</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#6b7280' }}>{parseFloat(row.total_overs || 0).toFixed(1)}</div>
                    <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>Overs</div>
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0 }}>
                    {isExpanded ? '▲' : '▼'}
                  </div>
                </div>
                {isExpanded && <div style={{ marginBottom: 8 }}><Drilldown breakdown={breakdown} loading={breakdownLoading} activeTab={activeTab} /></div>}
              </div>
              );
            })}
          </div>
        )}

        {/* Normal leaderboard list for all other tabs */}
        {activeTab !== 'woodstrike' && activeTab !== 'maidens' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            No data yet — import a scorecard first
          </div>
        ) : sorted.map((row, i) => {
          const stat = getStatValue(row, activeTab);
          const isMeRow = isMe(row);
          const isGuest = !!row.is_guest;
          const isExpanded = !isGuest && expandedMember === row.id;
          const medal = !isGuest && i < 3 ? medalColors[i] : null;
          const rowKey = isGuest ? `guest-${row.display_name}` : row.id;

          return (
            <div key={rowKey}>
              <div onClick={() => !isGuest && loadBreakdown(row.id)}
                style={{
                  display: 'flex', flexDirection: 'column',
                  padding: '12px 14px', marginBottom: 2,
                  background: isGuest ? '#fafaf9' : isMeRow ? '#eff6ff' : 'white',
                  borderRadius: isExpanded ? '12px 12px 0 0' : 12,
                  border: isGuest ? '1px dashed #e5e7eb' : isMeRow ? '1.5px solid #bfdbfe' : '1px solid #f1f5f9',
                  cursor: isGuest ? 'default' : 'pointer',
                  borderLeft: isGuest ? '4px solid #e5e7eb' : medal ? `4px solid ${medal}` : isMeRow ? '4px solid #3b82f6' : '4px solid transparent',
                  opacity: isGuest ? 0.85 : 1,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Rank */}
                <div style={{
                  width: 28, textAlign: 'center',
                  fontFamily: 'Rajdhani,sans-serif', fontWeight: 800,
                  fontSize: medal ? '1.2rem' : '0.9rem',
                  color: medal || '#9ca3af',
                }}>
                  {isGuest ? 'G' : medal ? medalEmojis[i] : i + 1}
                </div>

                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: isGuest ? 'linear-gradient(135deg, #9ca3af, #6b7280)' : `linear-gradient(135deg, #1E3A6E, #2d5699)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontFamily: 'Rajdhani,sans-serif',
                  fontWeight: 800, fontSize: '1rem', flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  {row.photo_url
                    ? <img src={row.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : row.display_name?.[0]?.toUpperCase()
                  }
                </div>

                {/* Name + sub */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Rajdhani,sans-serif', fontWeight: 800,
                    fontSize: '1rem', color: isGuest ? '#6b7280' : '#1E3A6E',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {row.display_name}
                    {isMeRow && <span style={{ fontSize: '0.65rem', background: '#dbeafe', color: '#1d4ed8', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>YOU</span>}
                    {isGuest && <span style={{ fontSize: '0.6rem', background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 10, fontWeight: 700, border: '1px solid #e5e7eb' }}>GUEST</span>}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 1 }}>
                    {activeTab === 'batting'
                      ? `${parseInt(row.total_runs)||0}r · ${parseInt(row.total_balls)||0}b · ${parseInt(row.total_fours)||0}x4 · ${parseInt(row.total_sixes)||0}x6`
                      : activeTab === 'bowling'
                      ? `${parseInt(row.total_wickets)||0}w · ${parseFloat(row.total_overs||0).toFixed(1)}ov · ${parseInt(row.total_maidens)||0}m`
                      : activeTab === 'fielding'
                      ? `${parseInt(row.total_catches)||0}c · ${parseInt(row.total_stumpings)||0}st · ${parseInt(row.total_run_outs)||0}ro`
                      : activeTab === 'allrounder'
                      ? (() => {
                          const bPct = ((parseFloat(row.total_runs)||0)/totalRuns*100).toFixed(1);
                          const wPct = ((parseFloat(row.total_wickets)||0)/totalWkts*100).toFixed(1);
                          const dis = (parseInt(row.total_catches)||0)+(parseInt(row.total_stumpings)||0)+(parseInt(row.total_run_outs)||0);
                          const fPct = (dis/totalDis*100).toFixed(1);
                          return `Bat: ${bPct}% · Bowl: ${wPct}% · Field: ${fPct}%`;
                        })()
                      : (() => {
                          // UCC Points / Max MOM / Best Captain sub-line:
                          // • If the member has points in this season, show Bat/Bowl/Field points.
                          // • Otherwise (pre-2022 data, where points aren't computed per charter),
                          //   show raw stats as a meaningful fallback.
                          const bp = parseFloat(row.batting_pts)  || 0;
                          const wp = parseFloat(row.bowling_pts)  || 0;
                          const fp = parseFloat(row.fielding_pts) || 0;
                          if (bp + wp + fp > 0) {
                            return `Bat: ${bp.toFixed(1)} · Bowl: ${wp.toFixed(1)} · Field: ${fp.toFixed(1)}`;
                          }
                          // Raw stats fallback
                          const r  = parseInt(row.total_runs)    || 0;
                          const w  = parseInt(row.total_wickets) || 0;
                          const c  = parseInt(row.total_catches) || 0;
                          const s  = parseInt(row.total_stumpings) || 0;
                          const ro = parseInt(row.total_run_outs) || 0;
                          const dis = c + s + ro;
                          return `${r}r · ${w}w${dis > 0 ? ` · ${dis} dis` : ''}`;
                        })()
                    }
                  </div>
                </div>

                {/* Stat value — single big number (Net points for Points tab, stat for others) */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontFamily: 'Rajdhani,sans-serif', fontWeight: 900,
                    fontSize: '1.4rem', color: medal || (isMeRow ? '#1d4ed8' : '#1E3A6E'),
                    lineHeight: 1,
                  }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {activeTab === 'points' && viewMode !== 'match' && parseFloat(row.penalty_pts || 0) > 0 ? 'NET' : stat.label}
                  </div>
                </div>

                {/* Expand indicator */}
                <div style={{ color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0 }}>
                  {isGuest ? '' : isExpanded ? '▲' : '▼'}
                </div>
                </div>

                {/* Penalty breakdown chip — only shown on Points tab season view when penalty > 0 */}
                {activeTab === 'points' && viewMode !== 'match' && parseFloat(row.penalty_pts || 0) > 0 && (() => {
                  const penalty = parseFloat(row.penalty_pts);
                  const gross = row.gross_pts != null
                    ? parseFloat(row.gross_pts)
                    : parseFloat(row.total_pts || 0) + penalty;
                  const net = gross - penalty;
                  return (
                    <div style={{
                      marginTop: 8, padding: '6px 10px',
                      background: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 8,
                      fontFamily: 'Rajdhani,sans-serif', fontSize: '0.74rem',
                      color: '#374151', fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 6, flexWrap: 'wrap',
                    }}>
                      <span>Gross <strong style={{ color: '#374151' }}>{gross.toFixed(1)} pts</strong></span>
                      <span style={{ color: '#9ca3af' }}>−</span>
                      <span>Penalty <strong style={{ color: '#dc2626' }}>{penalty.toFixed(0)} pts</strong></span>
                      <span style={{ color: '#9ca3af' }}>=</span>
                      <span>Net <strong style={{ color: '#1E3A6E' }}>{net.toFixed(1)} pts</strong></span>
                    </div>
                  );
                })()}
              </div>


              {/* Expanded drilldown — tab-aware renderer */}
              {isExpanded && (
                <Drilldown breakdown={breakdown} loading={breakdownLoading} activeTab={activeTab} />
              )}
            </div>
          );
        })
        )}
      </div>
    </div>
  );
}

export default LeaderboardsPage;
